import { ReactNode } from "react";

interface ThreeQuestionsProps {
  /** What changed since the Director was last here. One short sentence. */
  whatChanged: ReactNode;
  /** What needs their attention right now. One short sentence, or an "All clear" line. */
  needsAttention: ReactNode;
  /** What they should do next. One short sentence, ideally an action verb. */
  doNext: ReactNode;
  /** Optional small badge count (e.g. "3 items"). */
  attentionCount?: number;
}

/**
 * The Three Questions block.
 * Every admin page must answer these three questions within five seconds
 * (Executive Office Constitution + North Star). This is the mandatory
 * briefing card that sits directly under the PageHero on every page.
 */
export function ThreeQuestions({
  whatChanged,
  needsAttention,
  doNext,
  attentionCount,
}: ThreeQuestionsProps) {
  return (
    <section
      aria-label="Executive briefing"
      className="paper-card p-5 md:p-6 grid gap-4 md:grid-cols-3"
    >
      <Row label="What changed" value={whatChanged} />
      <Row
        label="Needs your attention"
        value={needsAttention}
        badge={attentionCount && attentionCount > 0 ? attentionCount : undefined}
        emphasis={!!attentionCount && attentionCount > 0}
      />
      <Row label="What to do next" value={doNext} />
    </section>
  );
}

function Row({
  label,
  value,
  badge,
  emphasis,
}: {
  label: string;
  value: ReactNode;
  badge?: number;
  emphasis?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="eyebrow flex items-center gap-2">
        <span>{label}</span>
        {typeof badge === "number" && (
          <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full text-[10px] font-semibold bg-terracotta/15 text-terracotta">
            {badge}
          </span>
        )}
      </div>
      <p
        className={`mt-2 text-sm leading-relaxed ${
          emphasis ? "text-foreground font-medium" : "text-foreground/85"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
